import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import wish from "../images/wishlist_nofill.svg";
import wishFilled from "../images/wish.svg";
import { useWishlist } from "../Context/WishlistContext";
import { useCart } from "../Context/Createcontext";
import { Helmet } from "react-helmet";
import Popup from "../Components/common/Popup";
import { api_url } from "../config/config";
import { useNavigate } from "react-router-dom";
import addToCartIcon from "../Icons/addToCartIcon.svg";
import { toast } from "react-hot-toast";
import { useApp } from "../Context/AppContext";
import HeroBannner from "./HeroBannner";
import { normalizeSearchQuery } from "../utils/normalizeSearch";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { toProductSlug } from "../utils/productSlug";

const SORT_OPTIONS = [
  { value: "low-to-high", label: "Price: Low → High" },
  { value: "high-to-low", label: "Price: High → Low" },
  { value: "discount", label: "Best Discount" },
];

const getPriceInfo = (variants, discountPercent) => {
  if (!variants || variants.length === 0)
    return { mrp: null, finalPrice: null, showDiscount: false, pct: 0 };
  const sorted = [...variants]
    .map((v) => ({
      ...v,
      _mrp: parseFloat(v.mrp) || 0,
      _disc: parseFloat(v.discounted_price) || 0,
    }))
    .filter((v) => v._mrp > 0)
    .sort((a, b) => a._mrp - b._mrp);
  if (!sorted.length)
    return { mrp: null, finalPrice: null, showDiscount: false, pct: 0 };
  const best = sorted[0];
  const showDiscount = best._disc > 0 && best._disc < best._mrp;
  return {
    mrp: best._mrp,
    finalPrice: showDiscount ? best._disc : best._mrp,
    showDiscount,
    pct: showDiscount
      ? discountPercent ||
        Math.round(((best._mrp - best._disc) / best._mrp) * 100)
      : 0,
  };
};

// The backend applies the selected sort before pagination. Keep that order
// when another page arrives so new cards are appended below the cards the
// customer is currently viewing. De-duplicate defensively in case the data
// changes between two page requests.
const appendUniqueProducts = (currentProducts, incomingProducts) => {
  const seen = new Set(
    currentProducts
      .map((product) => product.product_id ?? product.id)
      .filter((id) => id !== null && id !== undefined)
      .map(String),
  );

  return [
    ...currentProducts,
    ...incomingProducts.filter((product) => {
      const id = product.product_id ?? product.id;
      if (id === null || id === undefined) return true;
      const key = String(id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }),
  ];
};

const SortDropdown = ({ sortOrder, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current =
    SORT_OPTIONS.find((o) => o.value === sortOrder) || SORT_OPTIONS[0];
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 bg-white border border-[#e2eaea] hover:border-[#5fbdb3] text-[#374747] text-[13px] font-semibold px-4 h-10 rounded-xl transition-all duration-200 font-poppins whitespace-nowrap"
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="4" y1="6" x2="20" y2="6" />
          <line x1="8" y1="12" x2="16" y2="12" />
          <line x1="11" y1="18" x2="13" y2="18" />
        </svg>
        Sort: {current.label}
        <svg
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 bg-white border border-[#e2eaea] rounded-2xl shadow-[0_8px_32px_rgba(22,44,42,0.16)] overflow-hidden min-w-[200px]">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={`w-full flex items-center justify-between px-4 py-3 text-[13px] font-semibold text-left transition-all font-poppins ${sortOrder === opt.value ? "bg-[#eaf4f3] text-[#1e4a46]" : "text-[#374747] hover:bg-[#f8fafa] hover:text-[#1e4a46]"}`}
            >
              {opt.label}
              {sortOrder === opt.value && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1e4a46"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const AllProductsIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#5fbdb3"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="3" width="20" height="5" rx="1" />
    <rect x="2" y="10" width="20" height="5" rx="1" />
    <rect x="2" y="17" width="20" height="5" rx="1" />
  </svg>
);

const Allproducts = () => {
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const {
    handleAddToCart,
    showLoginPopup,
    setShowLoginPopup,
    handleRemoveFromCart,
    cartItems,
    setCartItems,
  } = useCart();
  const { contextCategories } = useApp();

  const navigate = useNavigate();
  const location = useLocation();

  const _preselected = location.state?.preselectedBrand ?? null;

  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [products, setProducts] = useState([]);
  const [allBrands, setAllBrands] = useState([]);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState(() =>
    _preselected ? new Set([_preselected.id]) : new Set(),
  );
  const [heroSearch, setHeroSearch] = useState(
    location.state?.initialSearch || "",
  );
  const debouncedHeroSearch = useDebouncedValue(heroSearch, 400);
  const [brandSearch, setBrandSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("low-to-high");
  const [manufactid, setManufactid] = useState(() =>
    _preselected ? [_preselected.id] : [],
  );
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(() => !!_preselected);
  const [tooltipOpen, setTooltipOpen] = useState(null);

  // Brand pagination states
  const [brandPage, setBrandPage] = useState(1);
  const [brandTotalPages, setBrandTotalPages] = useState(1);
  const [brandLoading, setBrandLoading] = useState(false);
  const [hasMoreBrands, setHasMoreBrands] = useState(true);

  const dropdownRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);
  const brandScrollRef = useRef(null);
  const searchInputRef = useRef(null);
  const productRequestGenerationRef = useRef(0);

  useEffect(() => {
    const h = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setSidebarOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  /* ── Sync search box if navigated here again with a new search term ── */
  useEffect(() => {
    if (location.state?.initialSearch) {
      setHeroSearch(location.state.initialSearch);
    }
  }, [location.state?.initialSearch]);

  /* ── Clearing the search must also clear the router's history state —
     otherwise location.state.initialSearch survives a page refresh (the
     browser keeps history.state for the current entry) and silently
     brings the old search term back. ── */
  const clearHeroSearch = () => {
    setHeroSearch("");
    if (location.state?.initialSearch) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    searchInputRef.current?.focus();
  };

  /* ── Fetch brands with pagination and search ── */
  const fetchBrands = async (page = 1, search = "", append = false) => {
    if (brandLoading) return;

    setBrandLoading(true);
    try {
      const params = new URLSearchParams({
        page: page,
        limit: 18,
      });

      if (search.trim()) {
        params.append("search", search.trim());
      }

      const response = await axios.get(
        `${api_url}/manufacturer?${params.toString()}`,
      );

      if (response.data?.results) {
        const formatted = response.data.results
          .filter((m) => m && m.manufacturer_id)
          .map((m) => ({
            id: m.manufacturer_id,
            name: m.manufacturer_name || m.name || "",
          }));

        if (append) {
          setAllBrands((prev) => [...prev, ...formatted]);
          setFilteredBrands((prev) => [...prev, ...formatted]);
        } else {
          setAllBrands(formatted);
          setFilteredBrands(formatted);
        }

        setBrandTotalPages(response.data.totalPages || 1);
        setHasMoreBrands(page < (response.data.totalPages || 1));
      }
    } catch (err) {
      console.error("Error fetching brands:", err);
    } finally {
      setBrandLoading(false);
    }
  };

  /* ── Initial brand fetch ── */
  useEffect(() => {
    fetchBrands(1, "");
  }, []);

  /* ── Brand search with debounce ── */
  useEffect(() => {
    const timer = setTimeout(() => {
      setBrandPage(1);
      setHasMoreBrands(true);
      fetchBrands(1, brandSearch, false);
    }, 500);

    return () => clearTimeout(timer);
  }, [brandSearch]);

  /* ── Infinite scroll for brands ── */
  const handleBrandScroll = (e) => {
    const element = e.target;
    const bottom =
      element.scrollHeight - element.scrollTop <= element.clientHeight + 50;

    if (bottom && hasMoreBrands && !brandLoading) {
      const nextPage = brandPage + 1;
      setBrandPage(nextPage);
      fetchBrands(nextPage, brandSearch, true);
    }
  };

  const fetchProducts = async (
    page = 1,
    append = false,
    requestGeneration = productRequestGenerationRef.current,
  ) => {
    if (page === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page,
        limit: 20,
        sort: sortOrder,
        instock: "true",
      });

      // Add search parameter
      const normalizedSearch = normalizeSearchQuery(debouncedHeroSearch);
      if (normalizedSearch) {
        params.append("search", normalizedSearch);
      }

      // Make POST request with filters in body
      const res = await axios.post(
        `${api_url}/all-product?${params.toString()}`,
        {
          category_ids: [...selectedCategories],
          manufacturer_ids: Array.isArray(manufactid) ? manufactid : [],
        },
        { headers: { "Content-Type": "application/json", "x-auth": "false" } },
      );

      const data = Array.isArray(res.data.data) ? res.data.data : [];
      const pagination = res.data.pagination || {};

      // A sort, search, or filter may have changed while this request was in
      // flight. Never let the obsolete response replace or contaminate the
      // new result set.
      if (requestGeneration !== productRequestGenerationRef.current) return;

      setTotalCount(pagination.total || data.length);
      setHasMore(page < (pagination.totalPages || 1));

      if (append) {
        setProducts((prev) => appendUniqueProducts(prev, data));
      } else {
        setProducts(data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      if (
        !append &&
        requestGeneration === productRequestGenerationRef.current
      ) {
        setProducts([]);
      }
    } finally {
      if (requestGeneration === productRequestGenerationRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  // Initial fetch + reset on filter/sort/search change
  useEffect(() => {
    const requestGeneration = productRequestGenerationRef.current + 1;
    productRequestGenerationRef.current = requestGeneration;
    setCurrentPage(1);
    setProducts([]);
    setHasMore(true);
    setLoadingMore(false);
    fetchProducts(1, false, requestGeneration);

    return () => {
      if (productRequestGenerationRef.current === requestGeneration) {
        productRequestGenerationRef.current += 1;
      }
    };
  }, [manufactid, selectedCategories, sortOrder, debouncedHeroSearch]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const triggerElement = loadMoreTriggerRef.current;
    if (!triggerElement || !hasMore || loadingMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          const nextPage = currentPage + 1;
          setCurrentPage(nextPage);
          fetchProducts(
            nextPage,
            true,
            productRequestGenerationRef.current,
          );
        }
      },
      {
        threshold: 0,
        rootMargin: "400px",
      },
    );

    observer.observe(triggerElement);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loadingMore, currentPage, loading]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSortChange = (order) => {
    setSortOrder(order);
  };

  const handleCategoryChange = (catId) => {
    setSelectedCategories((prev) => {
      const n = new Set(prev);
      n.has(catId) ? n.delete(catId) : n.add(catId);
      return n;
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBrandChange = (brandId, checked) => {
    setSelectedBrands((prev) => {
      const n = new Set(prev);
      checked ? n.add(brandId) : n.delete(brandId);
      return n;
    });
    setManufactid((prev) => {
      const n = checked
        ? [...prev, brandId]
        : prev.filter((id) => id !== brandId);
      return n;
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleClearAll = () => {
    setSelectedBrands(new Set());
    setManufactid([]);
    setSelectedCategories(new Set());
    setHeroSearch("");
    setBrandSearch("");
  };

  const handleClearCategories = () => {
    setSelectedCategories(new Set());
    // Any leftover brand search text should go too — otherwise it
    // silently keeps narrowing the brand list after categories are cleared.
    setBrandSearch("");
  };

  const handleProductClick = (product_id, product) =>
    navigate(`/products/${toProductSlug(product)}`, {
      state: { product_id },
    });

  const OutOfQuantityCheck = (variants) =>
    variants.every((v) => !v.quantity || v.quantity === 0);

  // Browsing is filtered by the backend. Search results can also contain
  // unavailable products and their cards render the out-of-stock state.
  const visibleProducts = products;

  const getCartQuantity = (variantId) => {
    const item = cartItems.find((ci) => ci.variant_id === variantId);
    return item ? item.quantity : 0;
  };

  const handleIncrease = async (e, variant, item) => {
    e.preventDefault();
    if (!variant?.variant_id) return;
    const qty = getCartQuantity(variant.variant_id);
    const res = await fetch(`${api_url}/cart`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
      body: JSON.stringify({
        product_id: variant.product_id,
        quantity: qty + 1,
        variant_id: variant.variant_id,
      }),
    });
    const data = await res.json();
    if (
      !res.ok ||
      data.message?.toLowerCase().includes("stock not available")
    ) {
      toast.error(`${item.name} is out of stock`);
      return;
    }
    setCartItems((prev) =>
      prev.map((ci) =>
        ci.variant_id === variant.variant_id
          ? { ...ci, quantity: ci.quantity + 1 }
          : ci,
      ),
    );
  };

  const handleDecrease = async (e, variant) => {
    e.preventDefault();
    if (!variant?.variant_id) return;
    const qty = getCartQuantity(variant.variant_id);
    if (qty === 1) {
      await handleRemoveFromCart(variant.product_id, variant.variant_id);
      setCartItems((prev) =>
        prev.filter((ci) => ci.variant_id !== variant.variant_id),
      );
      return;
    }
    const res = await fetch(`${api_url}/cart`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
      body: JSON.stringify({
        product_id: variant.product_id,
        quantity: qty - 1,
        variant_id: variant.variant_id,
      }),
    });
    const data = await res.json();
    if (
      !res.ok ||
      data.message?.toLowerCase().includes("stock not available")
    ) {
      toast.error("Something went wrong");
      return;
    }
    setCartItems((prev) =>
      prev.map((ci) =>
        ci.variant_id === variant.variant_id
          ? { ...ci, quantity: ci.quantity - 1 }
          : ci,
      ),
    );
  };

  const renderCard = (product, index) => {
    const variants = product.variant || [];
    const { mrp, finalPrice, showDiscount, pct } = getPriceInfo(
      variants,
      product.discount_percent,
    );
    const isWishlisted = isInWishlist(product.product_id);
    const isOutOfStock = OutOfQuantityCheck(variants);
    const cartQty = getCartQuantity(product.variant?.[0]?.variant_id);
    const isMultiVariant =
      Array.isArray(product.variant) && product.variant.length > 1;
    const isOrange = index % 2 === 1;
    const cardBg = isOrange
      ? "bg-gradient-to-b from-[#fff3ee] to-white"
      : "bg-gradient-to-b from-[#eaf4f3] to-white";
    const cardBorder = isOrange
      ? "border-[#ffd5c2] hover:border-[#ff601e]/60"
      : "border-[#e2eaea] hover:border-[#5fbdb3]";
    const accentBar = isOrange
      ? "from-[#ff601e] to-[#e55519]"
      : "from-[#1e4a46] via-[#5fbdb3] to-[#1e4a46]";
    const imgBg = isOrange ? "bg-[#fff8f5]" : "bg-[#f4fbfa]";
    const shadow = isOrange
      ? "shadow-[0_2px_12px_rgba(255,96,30,0.09)] hover:shadow-[0_10px_28px_rgba(255,96,30,0.22)]"
      : "shadow-[0_2px_12px_rgba(95,189,179,0.09)] hover:shadow-[0_10px_28px_rgba(95,189,179,0.22)]";
    const btnCls = isOrange
      ? "bg-[#ff601e] hover:bg-[#e55519] shadow-[0_2px_8px_rgba(255,96,30,0.3)]"
      : "bg-[#1e4a46] hover:bg-[#ff601e] shadow-[0_2px_8px_rgba(22,44,42,0.2)] hover:shadow-[0_4px_14px_rgba(255,96,30,0.4)]";
    const discCls = isOrange ? "text-[#ff601e]" : "text-[#5fbdb3]";

    return (
      <div
        key={product.product_id}
        className={`group border-[1.5px] ${cardBorder} ${cardBg} ${shadow} rounded-2xl overflow-hidden flex flex-col relative hover:-translate-y-2 transition-all duration-300 cursor-pointer`}
        style={{ animation: `fadeInUp 0.35s ease ${(index % 8) * 40}ms both` }}
      >
        <div
          className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${accentBar} opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10`}
        />
        {showDiscount && pct > 0 && (
          <div className="absolute top-3 right-3 z-10 bg-[#ff601e] text-white text-[9px] sm:text-[10px] font-bold w-9 h-9 rounded-full flex flex-col items-center justify-center shadow-[0_2px_8px_rgba(255,96,30,0.45)] leading-tight text-center">
            {pct}%<br />
            OFF
          </div>
        )}
        <button
          className="absolute top-3 left-3 z-10 w-7 h-7 sm:w-8 sm:h-8 bg-white rounded-xl border border-[#e2eaea] flex items-center justify-center shadow-sm hover:border-[#5fbdb3] hover:bg-[#eaf4f3] transition-all duration-200"
          onClick={(e) => {
            e.stopPropagation();
            if (!localStorage.getItem("authToken")) {
              setShowLoginPopup(true);
              return;
            }
            isWishlisted
              ? removeFromWishlist(
                  product.product_id,
                  product.variant?.[0]?.variant_id,
                )
              : addToWishlist(product, product.variant?.[0]);
          }}
          onMouseEnter={() => setTooltipOpen(index)}
          onMouseLeave={() => setTooltipOpen(null)}
        >
          <img
            src={isWishlisted ? wishFilled : wish}
            alt="wishlist"
            className="w-3.5 h-3.5 sm:w-4 sm:h-4"
          />
          {tooltipOpen === index && (
            <div className="absolute left-9 top-0 bg-[#162c2a] text-white text-[10px] font-medium px-2 py-1 rounded-lg whitespace-nowrap shadow z-30">
              {isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
            </div>
          )}
        </button>
        <div
          className={`w-full h-[250px] ${imgBg} overflow-hidden cursor-pointer group`}
          onClick={() => handleProductClick(product.product_id, product)}
        >
          <img
            src={
              product?.product_images?.image_url?.primary_image ||
              "/default.svg"
            }
            alt={product.name || "Pharmacy Product"}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/default.svg";
            }}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          />
        </div>
        <div className="w-full h-px bg-[#f1f5f5]" />
        <div
          className="px-3 sm:px-4 pt-3 pb-2 flex-1 flex flex-col gap-1.5 cursor-pointer"
          onClick={() => handleProductClick(product.product_id, product)}
        >
          <h3 className="font-semibold text-[12px] sm:text-[13px] text-[#1e4a46] line-clamp-2 leading-snug group-hover:text-[#ff601e] transition-colors duration-200">
            {product.name || "Unnamed Product"}
          </h3>
          <div className="flex items-baseline gap-2 flex-wrap">
            {mrp ? (
              <>
                {showDiscount && (
                  <span className="text-[11px] text-[#8faba9] line-through">
                    ₹{mrp}
                  </span>
                )}
                <span className="font-bold text-[14px] sm:text-[15px] text-[#1e4a46]">
                  ₹{finalPrice}
                </span>
                {showDiscount && pct > 0 && (
                  <span className={`text-[10px] font-semibold ${discCls}`}>
                    {pct}% off
                  </span>
                )}
              </>
            ) : (
              <span className="text-[12px] text-[#8faba9] font-medium">
                Price N/A
              </span>
            )}
          </div>
          {isOutOfStock && (
            <span className="self-start text-[10px] font-semibold text-red-500 bg-red-50 border border-red-100 rounded-lg px-2 py-0.5">
              Out of Stock
            </span>
          )}
        </div>
        <div className="px-3 sm:px-4 pb-4 pt-1">
          {isMultiVariant ? (
            <button
              className={`w-full h-10 ${btnCls} text-white text-[11px] sm:text-[12px] font-semibold rounded-xl flex items-center justify-center gap-1.5 transition-all duration-200 disabled:bg-[#c5d5d4] disabled:cursor-not-allowed`}
              onClick={(e) => {
                e.stopPropagation();
                if (!localStorage.getItem("authToken")) {
                  setShowLoginPopup(true);
                  return;
                }
                handleProductClick(product.product_id, product);
              }}
              disabled={isOutOfStock}
            >
              <img
                src={addToCartIcon}
                alt="Cart"
                className="w-3.5 h-3.5 object-contain"
              />
              Choose Variant
            </button>
          ) : cartQty > 0 ? (
            <div className="flex items-center justify-between bg-[#eaf4f3] border border-[#d4ecea] rounded-xl h-10 px-3">
              <button
                onClick={(e) => handleDecrease(e, product.variant[0], product)}
                className="w-7 h-7 flex items-center justify-center text-[#1e4a46] font-bold text-xl hover:bg-white rounded-lg transition-colors"
              >
                −
              </button>
              <span className="font-bold text-[14px] text-[#1e4a46] min-w-[24px] text-center">
                {cartQty}
              </span>
              <button
                onClick={(e) => handleIncrease(e, product.variant[0], product)}
                className="w-7 h-7 flex items-center justify-center text-[#1e4a46] font-bold text-xl hover:bg-white rounded-lg transition-colors"
              >
                +
              </button>
            </div>
          ) : (
            <button
              className={`w-full h-10 ${btnCls} disabled:bg-[#c5d5d4] disabled:cursor-not-allowed text-white text-[11px] sm:text-[13px] font-semibold rounded-xl flex items-center justify-center gap-1.5 transition-all duration-200`}
              onClick={async (e) => {
                e.stopPropagation();
                if (!localStorage.getItem("authToken")) {
                  setShowLoginPopup(true);
                  return;
                }
                await handleAddToCart(product, product.variant[0], 1);
              }}
              disabled={isOutOfStock}
            >
              <img
                src={addToCartIcon}
                alt="Cart"
                className="w-3.5 h-3.5 object-contain"
              />
              {isOutOfStock ? "Out of Stock" : "Add to Cart"}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f8fafa] font-poppins">
      <Helmet>
        <title>All Medicines | MediSYS Pharmacy</title>
        <meta
          name="description"
          content="MediSYS Pharmacy - Buy medicines online with fastest home delivery."
        />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <HeroBannner
        breadcrumbs={[
          { label: "Home", to: "/home" },
          { label: "All Medicines" },
        ]}
        title="All Medicines"
        subtitle={
          !loading
            ? `${totalCount.toLocaleString()} Medicine${totalCount !== 1 ? "s" : ""} available`
            : "Loading products…"
        }
        badge="Our Collection"
        icon={<AllProductsIcon />}
        accentColor="#5fbdb3"
      />

      <div className="bg-white border-b border-[#e2eaea] px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex items-center gap-2.5 flex-1 bg-[#f8fafa] border border-[#e2eaea] focus-within:border-[#5fbdb3] focus-within:bg-white rounded-xl px-4 h-10 transition-all duration-200">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#8faba9"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="flex-shrink-0"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              value={heroSearch}
              onChange={(e) => {
                const val = e.target.value;
                if (!val) {
                  clearHeroSearch();
                } else {
                  setHeroSearch(val);
                }
              }}
              placeholder="Search products…"
              className="flex-1 bg-transparent outline-none text-[13px] font-medium text-[#1e4a46] placeholder-[#8faba9] font-poppins"
            />
            {heroSearch && (
              <button
                onClick={clearHeroSearch}
                className="text-[#8faba9] hover:text-[#1e4a46] transition-colors"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden flex items-center gap-2 text-[13px] font-semibold text-[#1e4a46] border border-[#d4ecea] bg-[#eaf4f3] px-4 h-10 rounded-xl"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="8" y1="12" x2="20" y2="12" />
                <line x1="12" y1="18" x2="20" y2="18" />
              </svg>
              Filter
              {selectedBrands.size + selectedCategories.size > 0 && (
                <span className="bg-[#ff601e] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {selectedBrands.size + selectedCategories.size}
                </span>
              )}
            </button>
            <SortDropdown sortOrder={sortOrder} onChange={handleSortChange} />
            {!loading && (
              <div className="hidden sm:flex items-center gap-1.5 bg-[#eaf4f3] border border-[#d4ecea] rounded-xl px-3 h-10 flex-shrink-0">
                <span className="font-bold text-[13px] text-[#1e4a46]">
                  {/* {products.length.toLocaleString()} */}
                  {totalCount.toLocaleString()}
                </span>
                <span className="text-[12px] text-[#5fbdb3] font-medium">
                  {products.length === 1 ? "product" : "products"}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        <aside
          ref={dropdownRef}
          className={`${
            sidebarOpen ? "block" : "hidden"
          } lg:block lg:w-[260px] xl:w-[280px] flex-shrink-0 bg-white border-r border-[#e2eaea]`}
        >
          <div className="sticky top-0 p-5 space-y-6 max-h-[calc(100vh-120px)] scrollbar-hide overflow-y-auto">
            <div>
              <div className="flex items-center justify-between px-4 mb-3">
                <h3 className="font-bold text-[14px] text-[#ff601e]">
                  Categories
                </h3>
                {selectedCategories.size > 0 && (
                  <button
                    onClick={handleClearCategories}
                    className="text-[11px] font-semibold text-[#ff601e] hover:underline"
                  >
                    Clear ({selectedCategories.size})
                  </button>
                )}
              </div>

              <div className="flex flex-col gap-1 px-3 max-h-[280px] overflow-y-auto scrollbar-hide">
                {contextCategories.map((cat) => {
                  const checked = selectedCategories.has(cat.category_id);

                  return (
                    <label
                      key={cat.category_id}
                      className={`flex items-center gap-2.5 px-2 py-2.5 rounded-xl cursor-pointer transition-all ${
                        checked
                          ? "bg-[#eaf4f3] border border-[#d4ecea]"
                          : "hover:bg-[#f8fafa] border border-transparent"
                      }`}
                      onClick={() => handleCategoryChange(cat.category_id)}
                    >
                      <div
                        className={`w-4 h-4 rounded-md border-[1.5px] flex items-center justify-center flex-shrink-0 transition-all ${
                          checked
                            ? "bg-[#1e4a46] border-[#1e4a46]"
                            : "border-[#c5d5d4] bg-white"
                        }`}
                      >
                        {checked && (
                          <svg
                            width="9"
                            height="9"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="white"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </div>

                      <span
                        className={`text-[12px] ${
                          checked
                            ? "font-semibold text-[#1e4a46]"
                            : "text-[#374747]"
                        }`}
                      >
                        {cat.category_name}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="h-px bg-[#f1f5f5]" />

            <div className="px-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-[14px] text-[#ff601e]">Brands</h3>

                {selectedBrands.size > 0 && (
                  <button
                    onClick={handleClearAll}
                    className="text-[11px] font-semibold text-[#ff601e] hover:underline"
                  >
                    Clear ({selectedBrands.size})
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2 bg-[#f8fafa] border border-[#e2eaea] rounded-xl px-3 h-9 mb-3 focus-within:border-[#1e4a46] transition-all">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#8faba9"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>

                <input
                  type="text"
                  placeholder="Search brands…"
                  value={brandSearch}
                  onChange={(e) => setBrandSearch(e.target.value)}
                  className="flex-1 bg-transparent outline-none text-[12px] text-[#1a2625] placeholder-[#8faba9]"
                />
                {brandSearch && (
                  <button
                    type="button"
                    onClick={() => setBrandSearch("")}
                    className="text-[#8faba9] hover:text-[#1e4a46] transition-colors flex-shrink-0 cursor-pointer"
                  >
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>

              <div
                ref={brandScrollRef}
                onScroll={handleBrandScroll}
                className="flex flex-col gap-1 max-h-[260px] overflow-y-auto pr-1 scrollbar-hide"
              >
                {filteredBrands.map((brand) => {
                  const checked = selectedBrands.has(brand.id);

                  return (
                    <label
                      key={brand.id}
                      className={`flex items-center gap-2.5 px-2 py-2.5 rounded-xl cursor-pointer transition-all ${
                        checked
                          ? "bg-[#eaf4f3] border border-[#d4ecea]"
                          : "hover:bg-[#f8fafa] border border-transparent"
                      }`}
                      onClick={() => handleBrandChange(brand.id, !checked)}
                    >
                      <div
                        className={`w-4 h-4 rounded-md border-[1.5px] flex items-center justify-center flex-shrink-0 transition-all ${
                          checked
                            ? "bg-[#1e4a46] border-[#1e4a46]"
                            : "border-[#c5d5d4] bg-white"
                        }`}
                      >
                        {checked && (
                          <svg
                            width="9"
                            height="9"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="white"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </div>

                      <span
                        className={`text-[12px] ${
                          checked
                            ? "font-semibold text-[#1e4a46]"
                            : "text-[#374747]"
                        }`}
                      >
                        {brand.name}
                      </span>
                    </label>
                  );
                })}

                {/* Loading indicator */}
                {brandLoading && (
                  <div className="flex items-center justify-center py-2">
                    <svg
                      className="animate-spin"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="#5fbdb3"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                  </div>
                )}

                {filteredBrands.length === 0 && !brandLoading && (
                  <div className="px-2 py-3">
                    <p className="text-[12px] text-[#8faba9]">
                      No brands found.
                    </p>
                    {brandSearch && (
                      <button
                        onClick={() => setBrandSearch("")}
                        className="text-[11px] font-semibold text-[#ff601e] hover:underline mt-1 cursor-pointer"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </aside>

        <div className="flex-1 px-4 sm:px-6 lg:px-8 py-6">
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl border border-[#e2eaea] overflow-hidden animate-pulse flex flex-col"
                >
                  <div className="h-[160px] bg-[#e2eaea]" />
                  <div className="p-3 flex flex-col gap-2">
                    <div className="h-3 bg-[#e2eaea] rounded-full w-3/4" />
                    <div className="h-3 bg-[#e2eaea] rounded-full w-1/2" />
                    <div className="h-9 bg-[#e2eaea] rounded-xl mt-1" />
                  </div>
                </div>
              ))}
            </div>
          ) : visibleProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-16 h-16 rounded-2xl bg-[#eaf4f3] border border-[#d4ecea] flex items-center justify-center">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#5fbdb3"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <path d="M16 10a4 4 0 0 1-8 0" />
                </svg>
              </div>
              <p className="font-semibold text-[15px] text-[#374747]">
                No medicines found
              </p>
              <p className="text-[13px] text-[#8faba9]">
                Try changing your filters or search term.
              </p>
              <button
                onClick={handleClearAll}
                className="flex items-center gap-2 bg-[#1e4a46] hover:bg-[#162c2a] text-white font-semibold text-[13px] px-5 h-9 rounded-xl transition-all"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {visibleProducts.map((product, index) => renderCard(product, index))}
              </div>

              {/* Infinite scroll trigger */}
              <div
                ref={loadMoreTriggerRef}
                className="flex justify-center items-center py-8 min-h-[100px]"
              >
                {loadingMore && (
                  <div className="flex items-center gap-2 text-[#5fbdb3] text-[13px] font-medium">
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Loading more products...
                  </div>
                )}
                {!hasMore && products.length > 0 && (
                  <div className="text-[13px] text-[#8faba9] font-medium">
                    You've reached the end of the product list
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {showLoginPopup && <Popup onClose={() => setShowLoginPopup(false)} />}
      <style>{`@keyframes fadeInUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
};

export default Allproducts;
